// Keeps auth credentials out of third-party telemetry (Sentry, PostHog).
//
// Supabase's implicit flow, the cross-subdomain SSO handoff and several
// auth/invite links put credentials in the page URL (`#access_token=…`,
// `?token_hash=…`, `?accept_invite=…`, OAuth `?code=…`). Sentry and PostHog
// initialise before the app strips them, so every URL-bearing field of every
// event, breadcrumb and recording is scrubbed here before it is sent.

const SENSITIVE_PARAM_NAMES = [
  "access_token",
  "refresh_token",
  "provider_token",
  "provider_refresh_token",
  "id_token",
  "token_hash",
  "token",
  "accept_invite",
  "code",
  "otp",
  "apikey",
  "api_key",
  "password",
  "secret",
];

export const FILTERED = "[Filtered]";

// `(^|[^A-Za-z0-9_])` instead of a lookbehind so older Safari keeps working.
const SENSITIVE_PARAM_PATTERN = new RegExp(
  `(^|[^A-Za-z0-9_])(${SENSITIVE_PARAM_NAMES.join("|")})=([^&#\\s"'<>]*)`,
  "gi",
);
const JWT_PATTERN =
  /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g;
const BEARER_PATTERN = /\b(Bearer)\s+[A-Za-z0-9._~+/-]+=*/gi;
const SUPABASE_KEY_PATTERN = /\bsb_(?:secret|publishable)_[A-Za-z0-9_-]{8,}/g;

const MAX_DEPTH = 12;
const MAX_NODES = 20000;

export function scrubText(value) {
  if (typeof value !== "string" || value.length === 0) return value;
  return value
    .replace(
      SENSITIVE_PARAM_PATTERN,
      (_match, prefix, name) => `${prefix}${name}=${FILTERED}`,
    )
    .replace(JWT_PATTERN, FILTERED)
    .replace(BEARER_PATTERN, `$1 ${FILTERED}`)
    .replace(SUPABASE_KEY_PATTERN, FILTERED);
}

/**
 * Scrubs every string inside a telemetry payload, in place (bounded depth and
 * node count; anything past the budget is left untouched rather than cloned).
 */
export function scrubTelemetryPayload(payload) {
  let budget = MAX_NODES;
  const seen = new WeakSet();

  const visit = (value, depth) => {
    if (typeof value === "string") return scrubText(value);
    if (!value || typeof value !== "object" || depth > MAX_DEPTH) return value;
    if (seen.has(value) || budget <= 0) return value;
    seen.add(value);

    if (Array.isArray(value)) {
      for (let index = 0; index < value.length && budget > 0; index += 1) {
        budget -= 1;
        value[index] = visit(value[index], depth + 1);
      }
      return value;
    }

    for (const key of Object.keys(value)) {
      if (budget <= 0) break;
      budget -= 1;
      const scrubbed = visit(value[key], depth + 1);
      if (scrubbed !== value[key]) {
        try {
          value[key] = scrubbed;
        } catch {
          // Frozen/readonly field: leave it.
        }
      }
    }
    return value;
  };

  return visit(payload, 0);
}

const scrubAndKeep = (payload) => scrubTelemetryPayload(payload);

/** Spread into `Sentry.init`. */
export const sentryPrivacyOptions = {
  beforeSend: scrubAndKeep,
  beforeSendTransaction: scrubAndKeep,
  beforeBreadcrumb: scrubAndKeep,
  beforeSendLog: scrubAndKeep,
};

/** Spread into `Sentry.replayIntegration`. Recordings carry page URLs too. */
export const replayPrivacyOptions = {
  beforeAddRecordingEvent: scrubAndKeep,
};

/**
 * `beforeSend` does not run for replay events, but global event processors
 * do (they run in `prepareEvent` for every event type), so this covers
 * `replay_event.urls` as well as errors and transactions.
 */
export function installSentryEventScrubber(sentry) {
  sentry.addEventProcessor((event) => scrubTelemetryPayload(event));
}

/** Spread into `posthog.init`. */
export const posthogPrivacyOptions = {
  disable_capture_url_hashes: true,
  before_send: (event) => (event ? scrubTelemetryPayload(event) : event),
};
